import { Fragment, useCallback, useEffect, useState } from "react";
import { CheckCircle, Warning } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { FpShell } from "@/pages/panchatanthra/fp/FpShell";
import { fpBase, inr, monthLabel, useFpContext } from "@/lib/fp";
import { toast } from "sonner";

/** Budgets the way the business sets them, month by month.
 *  The annual baseline is the year's template; opening a month pre-fills it from the previous
 *  saved month (the carry-forward chain) so only the cells that vary are edited, and saving a
 *  month stores its full effective figures as their own versioned snapshot. Variable cost is
 *  always a percentage of sales — a component × category matrix for production and a
 *  component × sub-channel matrix for distribution — while revenue and fixed cost stay in
 *  absolute rupees. */

const COGS_LABEL = "Raw Material COGS";   // the single budgeted consumption figure per category

const NumberCell = ({ value, onChange, testid, disabled, suffix }) => (
  <div className="flex items-center justify-end gap-1">
    <input type="number" step="any" value={value ?? ""} disabled={disabled} data-testid={testid}
           onChange={(e) => onChange(e.target.value)}
           className="w-full min-w-20 rounded-lg border border-border bg-card px-2.5 py-1.5 text-right font-mono text-xs tabular-nums text-navy outline-none focus:border-terracotta disabled:opacity-60" />
    {suffix && <span className="text-[10px] text-muted-foreground">{suffix}</span>}
  </div>
);

const clean = (o) => Object.fromEntries(Object.entries(o || {})
  .filter(([, v]) => v !== "" && Number(v) !== 0).map(([k, v]) => [k, Number(v)]));
const cleanNested = (o) => Object.fromEntries(Object.entries(o || {})
  .map(([k, v]) => [k, clean(v)]).filter(([, v]) => Object.keys(v).length));
// fixed cost lines keep their zeros: a created line must survive a save before it is typed
const keepZeroNested = (o) => Object.fromEntries(Object.entries(o || {})
  .map(([k, v]) => [k, Object.fromEntries(Object.entries(v || {})
    .map(([n, val]) => [n, Number(val) || 0]))])
  .filter(([, v]) => Object.keys(v).length));

/* ------------------------------------------------ variable-cost matrix (component × axis) */
const Matrix = ({ testid, axisLabel, columns, rows, control, components, onControl, onCell,
                  disabled }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[640px] text-xs" data-testid={testid}>
      <thead>
        <tr className="bg-muted/40 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
          <th className="px-4 py-2 text-left">Component \ {axisLabel}</th>
          {columns.map((c) => (
            <th key={c} className="px-3 py-2 text-right">{c}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {rows.map((comp) => (
          <tr key={comp} data-testid={`${testid}-comp-${comp.replace(/\W+/g, "-")}`}>
            <td className="px-4 py-1.5 text-navy">{comp}</td>
            {columns.map((c) => (
              <td key={c} className="px-2 py-1.5">
                <NumberCell suffix="%" disabled={disabled}
                            testid={`${testid}-cell-${comp.replace(/\W+/g, "-")}-${c.replace(/\W+/g, "-")}`}
                            value={(components[c] || {})[comp]}
                            onChange={(v) => onCell(c, comp, v)} />
              </td>
            ))}
          </tr>
        ))}
        <tr>
          <td className="px-4 py-1.5 text-[10px] text-muted-foreground">
            Components total — % of own {axisLabel} sales
          </td>
          {columns.map((c) => {
            const s = Object.values(components[c] || {}).reduce((a, v) => a + (Number(v) || 0), 0);
            return (
              <td key={c} className="px-3 py-1.5 text-right font-mono text-[10px] tabular-nums text-muted-foreground"
                  data-testid={`${testid}-compsum-${c.replace(/\W+/g, "-")}`}>
                {s ? `${s.toFixed(2)}%` : "—"}
              </td>
            );
          })}
        </tr>
        <tr className="bg-muted/40">
          <td className="px-4 py-1.5 font-semibold text-navy">
            {axisLabel} control — % of total revenue
          </td>
          {columns.map((c) => (
            <td key={c} className="px-2 py-1.5">
              <NumberCell suffix="%" disabled={disabled}
                          testid={`${testid}-control-${c.replace(/\W+/g, "-")}`}
                          value={control[c]} onChange={(v) => onControl(c, v)} />
            </td>
          ))}
        </tr>
      </tbody>
    </table>
    <p className="mt-2 text-[10px] text-muted-foreground">
      A component % applies to that {axisLabel}'s own sales; the control row is the {axisLabel}'s
      whole allowance against total sales. When only components are set, their sum becomes the
      control.
    </p>
  </div>
);

/* ------------------------------------------------------------- fixed cost, absolute rupees */
const Tie = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1 text-[10px] ${ok ? "text-success" : "text-[hsl(28_82%_38%)]"}`}>
    {ok ? <CheckCircle size={11} /> : <Warning size={11} />} {label}
  </span>
);

const FixedSection = ({ testid, functions, lines, control, linesByFn, onControl, onLine,
                        disabled, engineLines = {}, onAddAll, onRemoveLine }) => {
  const sumLines = (fn) => Object.values(linesByFn[fn] || {})
    .reduce((s, v) => s + (Number(v) || 0), 0);
  const grandLines = functions.reduce((s, fn) => s + sumLines(fn), 0);
  const grandControl = functions.reduce((s, fn) => s + (Number(control[fn]) || 0), 0);
  return (
  <div>
  <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2" data-testid={testid}>
    {functions.map((fn) => {
      const ctl = Number(control[fn]) || 0;
      const linesTotal = Object.values(linesByFn[fn] || {}).reduce((s, v) => s + (Number(v) || 0), 0);
      const hasLines = Object.keys(linesByFn[fn] || {}).length > 0;
      // the line items this function actually carries in the engine's own Fixed Cost sheets
      const fromEngine = engineLines[fn] || [];
      const missing = fromEngine.filter((l) => (linesByFn[fn] || {})[l] === undefined);
      const options = [...new Set([...fromEngine, ...(lines || [])])];
      return (
        <div key={fn} className="rounded-lg border border-border p-3"
             data-testid={`${testid}-fn-${fn.replace(/\W+/g, "-")}`}>
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-navy">{fn}</p>
            <div className="w-36">
              <NumberCell value={control[fn]} disabled={disabled}
                          testid={`${testid}-total-${fn.replace(/\W+/g, "-")}`}
                          onChange={(v) => onControl(fn, v)} />
            </div>
          </div>
          <div className="mt-3 space-y-2 border-t border-border pt-3">
            {Object.keys(linesByFn[fn] || {})
              .sort((a, b) => a.localeCompare(b))
              .map((l) => (
                <div key={l} className="flex items-center justify-between gap-3">
                  <p className="min-w-0 flex-1 break-words text-xs text-navy">{l}</p>
                  <div className="flex items-center gap-1">
                    <div className="w-32">
                      <NumberCell value={(linesByFn[fn] || {})[l]} disabled={disabled}
                                  testid={`${testid}-line-${fn.replace(/\W+/g, "-")}-${l.replace(/\W+/g, "-")}`}
                                  onChange={(v) => onLine(fn, l, v)} />
                    </div>
                    {!disabled && (
                      <button title="Remove this line" onClick={() => onRemoveLine(fn, l)}
                              data-testid={`${testid}-rmline-${fn.replace(/\W+/g, "-")}-${l.replace(/\W+/g, "-")}`}
                              className="rounded-full border border-border px-1.5 py-0.5 text-[10px] leading-none text-muted-foreground transition hover:bg-muted">
                        ×
                      </button>
                    )}
                  </div>
                </div>
              ))}
            {!disabled && (
              <div className="flex flex-wrap items-center gap-2">
                <select value="" data-testid={`${testid}-addline-${fn.replace(/\W+/g, "-")}`}
                        onChange={(e) => e.target.value && onLine(fn, e.target.value, 0)}
                        className="min-w-0 flex-1 rounded-md border border-dashed border-border bg-card px-2 py-1.5 text-[11px] text-muted-foreground focus:border-navy focus:outline-none">
                  <option value="">+ Add a line item</option>
                  {options
                    .filter((l) => (linesByFn[fn] || {})[l] === undefined)
                    .map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
                {missing.length > 0 && (
                  <button onClick={() => onAddAll(fn)}
                          data-testid={`${testid}-addall-${fn.replace(/\W+/g, "-")}`}
                          className="whitespace-nowrap rounded-full border border-navy px-2.5 py-1 text-[10px] font-semibold text-navy transition hover:bg-navy/10">
                    Add all {missing.length} from engine
                  </button>
                )}
              </div>
            )}
            <div className="flex items-center justify-between border-t border-dashed border-border pt-2"
                 data-testid={`${testid}-linestotal-${fn.replace(/\W+/g, "-")}`}>
              <span className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">Lines total</span>
              <span className="font-display text-sm font-semibold tabular-nums text-navy">{inr(linesTotal)}</span>
            </div>
            {hasLines && (
              <div className="flex items-center justify-between pt-1">
                <Tie ok={Math.abs(linesTotal - ctl) < 1}
                     label={Math.abs(linesTotal - ctl) < 1 ? "Lines tie to the function total"
                                                           : `Lines differ by ${inr(linesTotal - ctl)}`} />
                <span className="font-mono text-[10px] tabular-nums text-muted-foreground">
                  vs function total {inr(ctl)}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    })}
  </div>
  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-navy/[0.06] px-4 py-3"
       data-testid={`${testid}-grand-total`}>
    <p className="text-[10px] uppercase tracking-[0.12em] text-navy">Total fixed cost budget</p>
    <div className="flex flex-wrap items-center gap-4">
      <p className="text-xs text-muted-foreground">
        Lines entered: <strong className="font-display text-sm text-navy tabular-nums"
          data-testid={`${testid}-grand-lines`}>{inr(grandLines)}</strong>
      </p>
      <p className="text-xs text-muted-foreground">
        Function totals: <strong className="font-display text-sm text-navy tabular-nums"
          data-testid={`${testid}-grand-control`}>{inr(grandControl)}</strong>
      </p>
      <Tie ok={Math.abs(grandLines - grandControl) < 1}
           label={Math.abs(grandLines - grandControl) < 1
             ? "Lines tie to the function totals"
             : `Lines differ by ${inr(grandLines - grandControl)}`} />
    </div>
  </div>
  </div>
  );
};

export default function FpBudgets() {
  const { tenantId } = useFpContext();
  const [data, setData] = useState(null);
  const [goals, setGoals] = useState(null);       // coverage goal, stored with the engine setup
  const [savingGoal, setSavingGoal] = useState(false);
  const [mode, setMode] = useState("baseline");          // "baseline" or a "YYYY-MM" month
  const [form, setForm] = useState(null);
  const [carriedFrom, setCarriedFrom] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deriveMonth, setDeriveMonth] = useState("");
  const [preview, setPreview] = useState(null);
  const [deriving, setDeriving] = useState(false);
  const [applying, setApplying] = useState(false);

  const MATRIX_KEYS = ["pvc_pct_by_category", "pvc_pct_by_component",
                       "dvc_pct_by_channel", "dvc_pct_by_component",
                       "fixed_by_function", "fixed_lines_by_function", "revenue_split"];

  const load = useCallback(async (target) => {
    if (!tenantId) return;
    const m = target ?? mode;
    try {
      const { data: out } = await api.get(`${fpBase(tenantId)}/budgets`,
        { params: m === "baseline" ? {} : { period: m } });
      setData(out);
      if (m === "baseline") {
        const b = out.budget || {};
        setForm({
          revenue_annual: b.revenue_annual || 0,
          revenue_monthly: { ...(b.revenue_monthly || {}) },
          ...Object.fromEntries(MATRIX_KEYS.map((k) => [k, { ...(b[k] || {}) }])),
          notes: b.notes || "",
        });
        setCarriedFrom(null);
      } else {
        const e = out.effective || {};
        setForm({
          revenue: e.revenue ?? "",
          ...Object.fromEntries(MATRIX_KEYS.map((k) => [k, { ...(e[k] || {}) }])),
          notes: e.notes || "",
        });
        setCarriedFrom(e.carried_from || "baseline");
      }
    } catch (e2) { setData({ error: errMsg(e2) }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, mode]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!tenantId) return;
    api.get(`${fpBase(tenantId)}/setup`)
      .then(({ data: s }) => setGoals({
        can_configure: s.can_configure,
        setup: s.setup || {},
        ratio: (s.setup?.goals?.throughput_fixed_ratio) ?? (s.defaults?.goals?.throughput_fixed_ratio) ?? 4,
        overrides: { ...((s.setup?.goals || {}).overrides || {}) },
      }))
      .catch(() => setGoals(null));
  }, [tenantId]);

  const saveGoal = async () => {
    setSavingGoal(true);
    try {
      await api.put(`${fpBase(tenantId)}/setup`, {
        ...goals.setup,
        goals: {
          throughput_fixed_ratio: Number(goals.ratio) || 4,
          overrides: Object.fromEntries(Object.entries(goals.overrides || {})
            .filter(([k, v]) => (k || "").trim() && Number(v) > 0)
            .map(([k, v]) => [k.trim(), Number(v)])),
        },
      });
      toast.success("Coverage goal saved");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingGoal(false); }
  };

  useEffect(() => {
    if (!deriveMonth && (data?.months_with_budget || []).length) {
      setDeriveMonth(data.months_with_budget[0]);
    }
  }, [data, deriveMonth]);

  if (!form || !data) {
    return (
      <FpShell showPeriod={false} title="Budgets & targets">
        <p className="label-caps animate-pulse" data-testid="fp-budget-loading">
          {data?.error || "Loading budgets"}
        </p>
      </FpShell>
    );
  }

  const disabled = !data.can_edit;
  const isBaseline = mode === "baseline";
  const monthSaved = !isBaseline && (data.saved_months || []).includes(mode);
  const setFlat = (key, name, value) =>
    setForm((f) => ({ ...f, [key]: { ...f[key], [name]: value === "" ? "" : Number(value) } }));
  const setNested = (key, outer, inner, value) =>
    setForm((f) => ({ ...f, [key]: { ...f[key], [outer]: { ...(f[key][outer] || {}), [inner]: value === "" ? "" : Number(value) } } }));

  // fixed cost line items the engine already carries, function by function
  const engineLines = data.fixed_lines_available || {};
  const engineLineCount = Object.values(engineLines).reduce((s, v) => s + v.length, 0);
  const fixedFunctions = [...new Set([...(data.business_functions || []),
                                      ...Object.keys(engineLines),
                                      ...Object.keys(form.fixed_lines_by_function || {})])]
    .sort((a, b) => a.localeCompare(b));
  // creates the rows at zero so the client only has to type the numbers
  const addAllEngineLines = (only) => {
    setForm((f) => {
      const block = { ...f.fixed_lines_by_function };
      (only ? [only] : Object.keys(engineLines)).forEach((fn) => {
        const next = { ...(block[fn] || {}) };
        (engineLines[fn] || []).forEach((l) => { if (next[l] === undefined) next[l] = 0; });
        block[fn] = next;
      });
      return { ...f, fixed_lines_by_function: block };
    });
  };
  const removeFixedLine = (fn, line) => setForm((f) => {
    const next = { ...(f.fixed_lines_by_function[fn] || {}) };
    delete next[line];
    return { ...f, fixed_lines_by_function: { ...f.fixed_lines_by_function, [fn]: next } };
  });

  // channel / sub-channel revenue targets: {month: {leaf: rupees}}, tied to the month's total
  const setSplit = (month, leaf, v) => setForm((f) => ({
    ...f,
    revenue_split: { ...(f.revenue_split || {}),
                     [month]: { ...((f.revenue_split || {})[month] || {}),
                                [leaf]: v === "" ? "" : Number(v) } },
  }));
  const splitMonths = isBaseline ? (data.months || []) : [mode];
  const splitSum = (month) => (data.revenue_channels || []).reduce((s, g) => s +
    g.leaves.reduce((a, leaf) => a + (Number(((form.revenue_split || {})[month] || {})[leaf]) || 0), 0), 0);
  const monthControl = (month) => isBaseline
    ? (Number((form.revenue_monthly || {})[month]) || 0)
    : (Number(form.revenue) || 0);

  const monthlyTotal = Object.values(form.revenue_monthly || {})
    .reduce((s, v) => s + (Number(v) || 0), 0);
  const matches = Math.abs(monthlyTotal - Number(form.revenue_annual || 0)) < 1;
  const spreadEvenly = () => {
    const per = Number(form.revenue_annual || 0) / (data.months.length || 12);
    setForm((f) => ({ ...f, revenue_monthly:
      Object.fromEntries(data.months.map((m) => [m, Math.round(per * 100) / 100])) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        fiscal_year: data.fiscal_year,
        pvc_pct_by_category: clean(form.pvc_pct_by_category),
        pvc_pct_by_component: cleanNested(form.pvc_pct_by_component),
        dvc_pct_by_channel: clean(form.dvc_pct_by_channel),
        dvc_pct_by_component: cleanNested(form.dvc_pct_by_component),
        fixed_by_function: clean(form.fixed_by_function),
        fixed_lines_by_function: keepZeroNested(form.fixed_lines_by_function),
        revenue_split: cleanNested(form.revenue_split),
        notes: form.notes || "",
      };
      if (isBaseline) {
        Object.assign(body, { revenue_annual: Number(form.revenue_annual) || 0,
                              revenue_monthly: clean(form.revenue_monthly) });
      } else {
        Object.assign(body, { period: mode,
                              revenue_monthly: { [mode]: Number(form.revenue) || 0 } });
      }
      await api.put(`${fpBase(tenantId)}/budgets`, body);
      toast.success(isBaseline
        ? "Annual baseline saved as a new version"
        : `${monthLabel(mode)} budget saved — later months now carry it forward`);
      await load();
    } catch (e2) { toast.error(errMsg(e2)); }
    finally { setSaving(false); }
  };

  const runDerive = async () => {
    if (!deriveMonth) return;
    setDeriving(true);
    try {
      const { data: out } = await api.get(`${fpBase(tenantId)}/budgets/derive`,
        { params: { period: deriveMonth } });
      setPreview(out);
    } catch (e2) { toast.error(errMsg(e2)); }
    finally { setDeriving(false); }
  };

  const applyDerived = async () => {
    setApplying(true);
    try {
      await api.post(`${fpBase(tenantId)}/budgets/apply`, { period: deriveMonth });
      toast.success(`Baseline replaced from the ${monthLabel(deriveMonth)} budget statement`);
      setPreview(null);
      load();
    } catch (e2) { toast.error(errMsg(e2)); }
    finally { setApplying(false); }
  };

  return (
    <FpShell showPeriod={false}
      title={`Budgets & targets · ${data.fiscal_year}`}
      description="The annual baseline is the year's template. Opening a month pre-fills it from the previous saved month, so only the cells that vary are edited — and saving a month stores its own versioned snapshot. Variable cost is a percentage of sales (component × category for production, component × sub-channel for distribution); revenue and fixed cost are absolute rupees."
      actions={!disabled && (
        <button onClick={save} disabled={saving} data-testid="fp-budget-save"
                className="rounded-full bg-terracotta px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] text-white hover:bg-terracotta/90 disabled:opacity-60">
          {saving ? "Saving" : isBaseline ? "Save baseline" : `Save ${monthLabel(mode)}`}
        </button>
      )}
    >
      <div className="space-y-6" data-testid="fp-budgets">
        {/* -------------------------------------------- month selector with carry-forward */}
        <div className="panel p-5" data-testid="fp-budget-months">
          <p className="label-caps">Budget for</p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <button onClick={() => setMode("baseline")} data-testid="fp-budget-mode-baseline"
                    className={`rounded-full px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] transition ${
                      isBaseline ? "bg-navy text-white" : "border border-border bg-card text-navy hover:bg-muted"}`}>
              Annual baseline
            </button>
            {data.months.map((m) => {
              const saved = (data.saved_months || []).includes(m);
              const active = mode === m;
              return (
                <button key={m} onClick={() => setMode(m)} data-testid={`fp-budget-mode-${m}`}
                        className={`relative rounded-full px-4 py-1.5 text-[11px] font-semibold transition ${
                          active ? "bg-terracotta text-white"
                                 : "border border-border bg-card text-navy hover:bg-muted"}`}>
                  {monthLabel(m)}
                  {saved && (
                    <span className={`absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full ${
                      active ? "bg-white" : "bg-success"}`} />)}
                </button>
              );
            })}
          </div>
          {!isBaseline && (
            <p className="mt-3 text-[11px] text-muted-foreground" data-testid="fp-budget-carried">
              {monthSaved
                ? `${monthLabel(mode)} has its own saved budget${carriedFrom && carriedFrom !== "baseline" && carriedFrom !== mode ? ` · figures carried from ${monthLabel(carriedFrom)}` : ""}.`
                : `Pre-filled from ${carriedFrom && carriedFrom !== "baseline" ? monthLabel(carriedFrom) : "the annual baseline"} — edit what varies and save to give ${monthLabel(mode)} its own budget.`}
            </p>
          )}
        </div>

        {/* -------------------------------------------- derive from an uploaded statement */}
        {(data.months_with_budget || []).length > 0 && (
          <div className="panel p-6" data-testid="fp-budget-derive">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="label-caps">Budget statement</p>
                <h3 className="font-display text-lg text-navy">
                  Build the baseline from the uploaded budget
                </h3>
                <p className="mt-1 max-w-2xl text-[11px] text-muted-foreground">
                  The uploaded budget statement is read line by line through the approved mapping
                  master. Variable-cost lines become percentages of the month&rsquo;s actual
                  revenue; fixed-cost lines stay in rupees. Applying replaces the baseline with the
                  file&rsquo;s figures — month snapshots saved on top are kept.
                </p>
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <label className="block">
                  <span className="label-caps">Budget month</span>
                  <select value={deriveMonth} disabled={disabled}
                          onChange={(e) => { setDeriveMonth(e.target.value); setPreview(null); }}
                          data-testid="fp-budget-derive-month"
                          className="block rounded-full border border-border bg-card px-4 py-2 text-xs text-navy outline-none focus:border-terracotta disabled:opacity-60">
                    {data.months_with_budget.map((m) => (
                      <option key={m} value={m}>{monthLabel(m)}</option>
                    ))}
                  </select>
                </label>
                <button onClick={runDerive} disabled={disabled || deriving || !deriveMonth}
                        data-testid="fp-budget-derive-preview-btn"
                        className="rounded-full border border-terracotta px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] text-terracotta hover:bg-terracotta/10 disabled:opacity-50">
                  {deriving ? "Reading…" : "Preview from file"}
                </button>
                {preview && (
                  <button onClick={applyDerived} disabled={applying}
                          data-testid="fp-budget-derive-apply"
                          className="rounded-full bg-navy px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] text-white hover:bg-navy/90 disabled:opacity-50">
                    {applying ? "Applying…" : "Apply — replace baseline"}
                  </button>
                )}
              </div>
            </div>
            {preview && (
              <div className="mt-5 rounded-2xl border border-border bg-muted/30 p-5"
                   data-testid="fp-budget-derive-preview">
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  {[["Revenue target", preview.preview.revenue],
                    ["Production VC", preview.preview.pvc_total],
                    ["Distribution VC", preview.preview.dvc_total],
                    ["Fixed cost", preview.preview.fixed_total]].map(([label, value]) => (
                    <div key={label}>
                      <p className="label-caps">{label}</p>
                      <p className="font-mono text-sm font-semibold tabular-nums text-navy">
                        {inr(value)}
                      </p>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-[11px] text-muted-foreground"
                   data-testid="fp-budget-derive-coverage">
                  From {preview.source.filename} · {preview.preview.lines_recognised} lines routed
                  against {inr(preview.preview.actual_revenue)} of actual{" "}
                  {monthLabel(preview.period)} revenue
                  {preview.unrecognised.length
                    ? ` · ${preview.unrecognised.length} lines not routed`
                    : " · every line routed"}.
                </p>
                {(preview.warnings || []).map((w) => (
                  <p key={w} className="mt-2 text-[11px] text-[hsl(28_82%_38%)]">{w}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* -------------------------------------------- revenue, absolute rupees */}
        <div className="panel p-6" data-testid="fp-budget-revenue">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="label-caps">Revenue target — absolute ₹</p>
              <h3 className="font-display text-lg text-navy">
                {isBaseline ? "Annual and month by month" : `${monthLabel(mode)}`}
              </h3>
            </div>
            {isBaseline ? (
              <div className="flex flex-wrap items-end gap-4">
                <label className="block">
                  <span className="label-caps">Annual target</span>
                  <NumberCell value={form.revenue_annual} disabled={disabled}
                              testid="fp-budget-revenue-annual"
                              onChange={(v) => setForm((f) => ({ ...f, revenue_annual: v }))} />
                </label>
                {!disabled && (
                  <button onClick={spreadEvenly} data-testid="fp-budget-spread"
                          className="rounded-full border border-border px-4 py-2 text-[10px] uppercase tracking-[0.12em] text-navy hover:bg-muted">
                    Spread evenly
                  </button>
                )}
                <div className="text-right">
                  <p className="label-caps">Sum of months</p>
                  <p className={`font-mono text-sm font-semibold tabular-nums ${
                    matches ? "text-success" : "text-[hsl(28_82%_38%)]"}`}
                     data-testid="fp-budget-monthly-total">{inr(monthlyTotal)}</p>
                </div>
              </div>
            ) : (
              <label className="block">
                <span className="label-caps">{monthLabel(mode)} revenue target</span>
                <NumberCell value={form.revenue} disabled={disabled}
                            testid="fp-budget-revenue-month"
                            onChange={(v) => setForm((f) => ({ ...f, revenue: v }))} />
              </label>
            )}
          </div>
          {isBaseline && (
            <>
              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                {data.months.map((m) => (
                  <label key={m} className="block">
                    <span className="label-caps">{monthLabel(m)}</span>
                    <NumberCell value={form.revenue_monthly[m]} disabled={disabled}
                                testid={`fp-budget-month-${m}`}
                                onChange={(v) => setFlat("revenue_monthly", m, v)} />
                  </label>
                ))}
              </div>
              {!matches && (
                <p className="mt-3 text-[11px] text-[hsl(28_82%_38%)]">
                  The months do not add up to the annual target. Both are stored as entered — the
                  variance is shown so it is a deliberate management choice, not a silent rounding.
                </p>
              )}
            </>
          )}

          {/* ----------------------------- revenue split by channel / sub-channel */}
          {(data.revenue_channels || []).length > 0 && (
            <div className="mt-6 border-t border-border pt-5" data-testid="fp-budget-revenue-split">
              <p className="label-caps">
                Channel-wise revenue split — absolute ₹{!isBaseline && ` · ${monthLabel(mode)}`}
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Set targets at the sub-channel where you have them, at the channel otherwise. A
                month's split must tie to that month's revenue target — a gap is flagged, not
                reconciled away.
              </p>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full min-w-[640px] text-xs">
                  <thead>
                    <tr className="bg-muted/40 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                      <th className="px-4 py-2 text-left">Channel / sub-channel</th>
                      {splitMonths.map((m) => (
                        <th key={m} className="px-2 py-2 text-right">{monthLabel(m)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/60">
                    {(data.revenue_channels || []).map((g) => (
                      <Fragment key={g.channel}>
                        <tr className="bg-navy/[0.04]">
                          <td className="px-4 py-1.5 font-display text-sm text-navy"
                              colSpan={splitMonths.length + 1}
                              data-testid={`fp-budget-split-channel-${g.channel.replace(/\W+/g, "-")}`}>
                            {g.channel}
                          </td>
                        </tr>
                        {g.leaves.map((leaf) => (
                          <tr key={leaf}
                              data-testid={`fp-budget-split-row-${leaf.replace(/\W+/g, "-")}`}>
                            <td className="px-4 py-1 pl-8 text-navy">{leaf}</td>
                            {splitMonths.map((m) => (
                              <td key={m} className="px-1 py-1">
                                <NumberCell value={((form.revenue_split || {})[m] || {})[leaf]}
                                            disabled={disabled}
                                            testid={`fp-budget-split-${m}-${leaf.replace(/\W+/g, "-")}`}
                                            onChange={(v) => setSplit(m, leaf, v)} />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </Fragment>
                    ))}
                    <tr className="bg-muted/40 font-semibold text-navy"
                        data-testid="fp-budget-split-total">
                      <td className="px-4 py-2">Split total vs month's target</td>
                      {splitMonths.map((m) => {
                        const total = splitSum(m);
                        const target = monthControl(m);
                        const flagged = Math.abs(total - target) >= 1
                                        && (target !== 0 || total !== 0);
                        return (
                          <td key={m} className="px-2 py-2 text-right font-mono tabular-nums"
                              data-testid={`fp-budget-split-tie-${m}`}>
                            <div>{inr(total)}</div>
                            {flagged && (
                              <div className="text-[10px] font-normal text-[hsl(28_82%_38%)]">
                                differs by {inr(total - target)}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* -------------------------------------------- variable cost matrices */}
        <div className="panel p-6" data-testid="fp-budget-pvc">
          <p className="label-caps">Production variable cost budget{!isBaseline && ` · ${monthLabel(mode)}`}</p>
          <h3 className="font-display text-lg text-navy">Component × production category — % of sales</h3>
          {!data.product_categories.length ? (
            <p className="mt-3 text-sm text-muted-foreground">
              Add product categories under Mapping master → dimension masters first.
            </p>
          ) : (
            <div className="mt-4">
              <Matrix testid="fp-budget-pvc-matrix" axisLabel="category"
                      columns={data.product_categories} rows={data.pvc_components || []}
                      control={form.pvc_pct_by_category} components={form.pvc_pct_by_component}
                      disabled={disabled}
                      onControl={(c, v) => setFlat("pvc_pct_by_category", c, v)}
                      onCell={(c, comp, v) => setNested("pvc_pct_by_component", c, comp, v)} />
            </div>
          )}
        </div>

        <div className="panel p-6" data-testid="fp-budget-dvc">
          <p className="label-caps">Distribution variable cost budget{!isBaseline && ` · ${monthLabel(mode)}`}</p>
          <h3 className="font-display text-lg text-navy">Component × sub-channel (outlet) — % of sales</h3>
          {!data.distribution_channels.length ? (
            <p className="mt-3 text-sm text-muted-foreground">
              Add distribution channels under Mapping master → dimension masters first.
            </p>
          ) : (
            <div className="mt-4">
              <Matrix testid="fp-budget-dvc-matrix" axisLabel="sub-channel"
                      columns={data.distribution_channels} rows={data.dvc_components || []}
                      control={form.dvc_pct_by_channel} components={form.dvc_pct_by_component}
                      disabled={disabled}
                      onControl={(c, v) => setFlat("dvc_pct_by_channel", c, v)}
                      onCell={(c, comp, v) => setNested("dvc_pct_by_component", c, comp, v)} />
            </div>
          )}
        </div>

        {/* -------------------------------------------- fixed cost, absolute rupees */}
        <div className="panel p-6" data-testid="fp-budget-fixed">
          <p className="label-caps">Fixed cost budget — absolute ₹{!isBaseline && ` · ${monthLabel(mode)}`}</p>
          <p className="mt-1 text-[11px] text-muted-foreground">
            The business-function figure is the month's control total. Line items inside it should
            tie to it; a gap is flagged rather than averaged away.
          </p>
          {engineLineCount > 0 && !disabled && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-dashed border-border bg-muted/30 p-3">
              <p className="text-[11px] text-muted-foreground">
                Your engine carries{" "}
                <span className="font-semibold text-navy">{engineLineCount}</span>{" "}
                fixed cost line items across{" "}
                {Object.keys(data.fixed_lines_available || {}).length} business functions, taken
                from the Fixed Cost sheets already built for your months.
              </p>
              <button onClick={() => addAllEngineLines()}
                      data-testid="fp-budget-fixed-create-all"
                      className="whitespace-nowrap rounded-full bg-navy px-3.5 py-1.5 text-[11px] font-semibold text-white transition hover:bg-navy/90">
                Create all line items
              </button>
            </div>
          )}
          {!(data.business_functions || []).length && !engineLineCount ? (
            <p className="mt-3 text-sm text-muted-foreground">
              No business functions configured yet — they appear once payroll or expense data is loaded.
            </p>
          ) : (
            <FixedSection testid="fp-budget-fixedgrid"
                          functions={fixedFunctions} lines={data.fixed_cost_lines || []}
                          control={form.fixed_by_function} linesByFn={form.fixed_lines_by_function}
                          disabled={disabled}
                          engineLines={data.fixed_lines_available || {}}
                          onAddAll={addAllEngineLines} onRemoveLine={removeFixedLine}
                          onControl={(fn, v) => setFlat("fixed_by_function", fn, v)}
                          onLine={(fn, l, v) => setNested("fixed_lines_by_function", fn, l, v)} />
          )}
        </div>

        {/* -------------------------------------------- coverage goal (stored with setup) */}
        {goals && (
          <div className="panel p-6" data-testid="fp-budget-goal">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="label-caps">Performance goal</p>
                <h3 className="font-display text-lg text-navy">
                  Throughput to fixed expense coverage
                </h3>
                <p className="mt-1 max-w-2xl text-[11px] text-muted-foreground">
                  The goal every production category and sub-channel is measured against in the
                  Executive pack: throughput generated ÷ fixed expense carried. At or above the
                  goal reads On goal; below it is flagged.
                </p>
              </div>
              {goals.can_configure && (
                <button onClick={saveGoal} disabled={savingGoal} data-testid="fp-budget-goal-save"
                        className="rounded-full border border-terracotta px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] text-terracotta hover:bg-terracotta/10 disabled:opacity-60">
                  {savingGoal ? "Saving" : "Save goal"}
                </button>
              )}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <p className="text-sm text-navy">Throughput must be at least</p>
              <input type="number" min="0" step="0.5" disabled={!goals.can_configure}
                     value={goals.ratio} data-testid="fp-budget-goal-ratio"
                     onChange={(e) => setGoals((g) => ({ ...g, ratio: e.target.value }))}
                     className="w-24 rounded-full border border-border bg-card px-4 py-2 text-center text-sm text-navy outline-none focus:border-terracotta disabled:opacity-60" />
              <p className="text-sm text-navy">times the fixed expense it carries</p>
            </div>
            <div className="mt-5">
              <p className="label-caps">Per-line overrides</p>
              <div className="mt-3 space-y-2">
                {Object.entries(goals.overrides).map(([label, ratio], i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2">
                    <input value={label} disabled={!goals.can_configure}
                           placeholder="Line (e.g. Live kitchen)"
                           data-testid={`fp-budget-goal-override-label-${i}`}
                           onChange={(e) => setGoals((g) => {
                             const o = { ...g.overrides };
                             const v = o[label];
                             delete o[label];
                             o[e.target.value] = v;
                             return { ...g, overrides: o };
                           })}
                           className="w-56 rounded-full border border-border bg-card px-4 py-2 text-sm text-navy outline-none focus:border-terracotta disabled:opacity-60" />
                    <input type="number" min="0" step="0.5" value={ratio}
                           disabled={!goals.can_configure}
                           data-testid={`fp-budget-goal-override-ratio-${i}`}
                           onChange={(e) => setGoals((g) => ({ ...g,
                             overrides: { ...g.overrides, [label]: e.target.value } }))}
                           className="w-24 rounded-full border border-border bg-card px-4 py-2 text-center text-sm text-navy outline-none focus:border-terracotta disabled:opacity-60" />
                    <button disabled={!goals.can_configure}
                            data-testid={`fp-budget-goal-override-remove-${i}`}
                            onClick={() => setGoals((g) => {
                              const o = { ...g.overrides };
                              delete o[label];
                              return { ...g, overrides: o };
                            })}
                            className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground hover:text-destructive disabled:opacity-60">
                      Remove
                    </button>
                  </div>
                ))}
                <button disabled={!goals.can_configure} data-testid="fp-budget-goal-add-override"
                        onClick={() => setGoals((g) => ({ ...g,
                          overrides: { ...g.overrides, "": "" } }))}
                        className="text-[10px] font-bold uppercase tracking-[0.12em] text-terracotta hover:underline disabled:opacity-60">
                  + Add a per-line override
                </button>
              </div>
            </div>
          </div>
        )}

        <label className="block panel p-5">
          <span className="label-caps">Notes</span>
          <textarea value={form.notes || ""} disabled={disabled} rows={2}
                    data-testid="fp-budget-notes"
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    className="mt-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-navy outline-none focus:border-terracotta disabled:opacity-60" />
        </label>
      </div>
    </FpShell>
  );
}
